import { Reveal, MaskedLines } from "./Reveal";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const PRODUCTS = [
  {
    tag: "Home Protection",
    title: "Mortgage Protection Insurance",
    slug: "mortgage-protection-insurance",
    ctaLabel: "Mortgage Protection",
    body:
      "Help protect your family by paying off or reducing your mortgage if you pass away unexpectedly. Affordable coverage designed to help your loved ones remain in their home.",
    points: ["Protect your family's home", "Flexible coverage options", "Affordable monthly premiums"],
    img: "/brand/assets/product-mortgage-protection.jpg",
  },
  {
    tag: "Flexible & affordable",
    title: "Term Life",
    slug: "term-life-insurance",
    body:
      "Maximum coverage for a defined period — 10, 20, or 30 years. The most cost-effective way to protect a mortgage, income, and growing children.",
    points: ["Level premiums", "Convertible to permanent", "High coverage, low cost"],
    img: "https://images.pexels.com/photos/6964361/pexels-photo-6964361.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900",
  },
  {
    tag: "Lifelong & guaranteed",
    title: "Whole Life",
    slug: "whole-life-insurance",
    body:
      "Permanent protection that never expires, with guaranteed cash value that grows tax-deferred. A cornerstone for estate and legacy planning.",
    points: ["Guaranteed cash value", "Fixed premiums for life", "Dividend potential"],
    img: "https://images.pexels.com/photos/5638466/pexels-photo-5638466.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900",
  },
  {
    tag: "Adjustable & wealth-building",
    title: "Universal Life",
    slug: "universal-life-insurance",
    body:
      "Permanent coverage with flexible premiums and cash accumulation. Adapt your protection and funding as your financial picture evolves.",
    points: ["Flexible premiums", "Cash value growth", "Adjustable death benefit"],
    img: "https://images.pexels.com/photos/36729677/pexels-photo-36729677.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900",
  },
  {
    tag: "Tax-advantaged growth",
    title: "Indexed Universal Life",
    slug: "indexed-universal-life-insurance",
    body:
      "Permanent life insurance offering lifelong protection with the opportunity to build tax-advantaged cash value tied to a market index while protecting against market losses.",
    points: ["Tax-advantaged cash value", "Downside market protection", "Flexible premiums"],
    img: "/brand/assets/product-indexed-universal-life.jpg",
  },
  {
    tag: "Final Expenses",
    title: "Final Expense Insurance",
    slug: "final-expense-insurance",
    ctaLabel: "Final Expense",
    body:
      "Affordable permanent life insurance designed to help cover funeral costs, burial expenses, and other final bills—giving your family financial peace of mind.",
    points: ["Lifetime coverage", "Fixed premiums", "Fast approvals available"],
    img: "/brand/assets/product-final-expense.jpg",
  },
  {
    tag: "Guaranteed growth",
    title: "Fixed Rate Annuities",
    slug: "fixed-rate-annuities",
    body:
      "Grow your savings safely with a predictable, guaranteed interest rate — shielded from market volatility. A dependable foundation for retirement income.",
    points: ["Principal protection", "Guaranteed fixed rate", "Tax-deferred growth"],
    img: "https://images.pexels.com/photos/6919723/pexels-photo-6919723.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900",
  },
  {
    tag: "Income for life",
    title: "Retirement Solutions",
    slug: "retirement-solutions",
    body:
      "Strategies designed to turn your savings into reliable, lasting income — so you can retire with confidence and never outlive your money.",
    points: ["Lifetime income planning", "Tax-efficient strategies", "Legacy preservation"],
    img: "https://images.pexels.com/photos/8170257/pexels-photo-8170257.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900",
  },
  {
    tag: "Freedom from debt",
    title: "Debt Free 4-Life",
    slug: "debt-free-4-life",
    body:
      "A structured plan to eliminate debt years faster and stay debt-free for life — freeing up income to build real, lasting wealth for your family.",
    points: ["Pay off debt faster", "No added monthly cost", "Build lasting wealth"],
    img: "https://images.pexels.com/photos/6995145/pexels-photo-6995145.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900",
  },
];

export const Products = () => {
  return (
    <section id="products" data-testid="products-section" className="relative bg-[#0a0a0a] py-16 lg:py-24 border-t border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-center gap-3 mb-8">
          <span className="mono text-[10px] uppercase tracking-[0.35em] text-[#d4af37]">Coverage</span>
          <span className="h-px flex-1 bg-white/10" />
          <span className="mono text-[10px] uppercase tracking-[0.35em] text-[#737373]">Protect · Grow · Retire</span>
        </div>

        <Reveal className="mb-10 max-w-3xl">
          <h2 className="serif font-light leading-[1.02] text-[#f4f0ea] text-4xl sm:text-5xl lg:text-6xl">
            <MaskedLines lines={[<>The right solution,</>, <><span className="italic text-[#d4af37]">tailored</span> to your life.</>]} />
          </h2>
        </Reveal>

        <Reveal delay={0.05} className="mb-10 max-w-2xl">
          <p className="text-[#a3a3a3] leading-relaxed tracking-wide">
            Serving families in <Link to="/life-insurance-chandler-az" data-testid="products-chandler-link" className="text-[#d4af37] hover:text-[#f0d57d] link-underline">Chandler, Arizona</Link> and in select states — brokered independently across 25+ A-rated or better insurance carriers.
          </p>
        </Reveal>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {PRODUCTS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.1}>
              <Link
                to={`/products/${p.slug}`}
                data-testid={`product-card-${p.slug}`}
                className="group relative flex flex-col h-full bg-[#030303] border border-white/10 rounded-sm overflow-hidden hover:border-[#d4af37]/40 transition-colors duration-500"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={p.img}
                    alt={p.title}
                    loading="lazy"
                    className="w-full h-full object-cover grayscale contrast-125 group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#030303] via-[#030303]/30 to-transparent" />
                  <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/70 to-transparent" />
                  <span className="absolute top-5 left-5 mono text-[10px] uppercase tracking-[0.25em] text-[#f0d57d] bg-black/55 backdrop-blur-md border border-white/10 rounded-full px-3 py-1.5">{p.tag}</span>
                </div>

                <div className="flex flex-col flex-1 p-8 lg:p-10">
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="serif text-3xl text-[#f4f0ea]">{p.title}</h3>
                    <ArrowUpRight className="text-[#737373] group-hover:text-[#d4af37] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-400" size={22} />
                  </div>
                  <p className="text-[#a3a3a3] leading-relaxed tracking-wide mb-8">{p.body}</p>
                  <ul className="mt-auto space-y-3 border-t border-white/10 pt-6">
                    {p.points.map((pt) => (
                      <li key={pt} className="flex items-center gap-3 text-sm text-[#f4f0ea]/80">
                        <span className="h-1 w-1 rounded-full bg-[#d4af37]" />
                        <span className="tracking-wide">{pt}</span>
                      </li>
                    ))}
                  </ul>
                  <span
                    data-testid={`product-cta-${p.slug}`}
                    className="mt-8 mono text-xs uppercase tracking-[0.2em] text-[#d4af37] link-underline w-fit"
                  >
                    Learn More →
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
